import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, FileText, Receipt, Send } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { apiError, fmtDate, fmtMoney, type IndustryTenant } from '../lib/industria'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
]

type Ordem = {
  id: string; number: string; lotNumber: string | null
  qtyProduced: number; closedAt: string | null
  productSku: string | null; productName: string | null
}

type Previa = {
  tenantName: string | null; year: number; month: number
  ordens: Ordem[]; unidades: number; precoPorUnidade: number; total: number
}

type Fatura = {
  id: string; tenantId: string; tenantName: string | null
  status: 'draft' | 'sent' | 'paid' | 'cancelled'
  total: string; generatedAt: string
  periodYear: number; periodMonth: number; itens: number
}

const STATUS_LABEL: Record<Fatura['status'], string> = {
  draft: 'Rascunho', sent: 'Enviada', paid: 'Paga', cancelled: 'Cancelada',
}
const STATUS_BADGE: Record<Fatura['status'], 'default' | 'success' | 'warning' | 'info'> = {
  draft: 'default', sent: 'info', paid: 'success', cancelled: 'warning',
}

// Fatura da indústria — documento próprio, separado do faturamento do
// fulfillment. Fabricar por encomenda e armazenar-e-expedir são contratos
// diferentes; misturá-los obrigava a explicar dois serviços numa conta só.
export function IndustriaFaturamentoPage() {
  const queryClient = useQueryClient()
  const hoje = new Date()

  const [tenantId, setTenantId] = useState('')
  const [year, setYear] = useState(hoje.getFullYear())
  const [month, setMonth] = useState(hoje.getMonth() + 1)
  const [preco, setPreco] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)

  const { data: tenants = [] } = useQuery<IndustryTenant[]>({
    queryKey: ['industria', 'tenants'],
    queryFn: async () => (await api.get('/materials/tenants')).data,
  })

  const { data: previa } = useQuery<Previa>({
    queryKey: ['industria', 'previa-fatura', tenantId, year, month, preco],
    queryFn: async () => (await api.get('/industry-invoice-preview', {
      params: { tenantId, year, month, unitPrice: Number(preco || 0) },
    })).data,
    enabled: !!tenantId,
  })

  const { data: faturas = [] } = useQuery<Fatura[]>({
    queryKey: ['industria', 'faturas', tenantId],
    queryFn: async () => (await api.get('/industry-invoices', {
      params: { tenantId: tenantId || undefined },
    })).data,
  })

  const gerar = useMutation({
    mutationFn: async () => (await api.post('/industry-invoices', {
      tenantId, year, month, unitPrice: Number(preco || 0),
    })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['industria', 'faturas'] })
      setOk('Fatura gerada.')
      setTimeout(() => setOk(null), 4000)
    },
    onError: (e) => setError(apiError(e, 'Não foi possível gerar a fatura')),
  })

  const mudarStatus = useMutation({
    mutationFn: async (v: { id: string; status: 'sent' | 'paid' | 'cancelled' }) =>
      (await api.patch(`/industry-invoices/${v.id}`, { status: v.status })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['industria', 'faturas'] }),
    onError: (e) => setError(apiError(e, 'Não foi possível mudar o status')),
  })

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Faturamento da indústria</h2>
        <p className="text-sm text-gray-500">
          Cobrança por unidade produzida — documento separado da fatura de fulfillment
        </p>
      </div>

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}
      {ok && <p className="rounded-lg bg-green-50 px-4 py-2 text-sm text-green-800">{ok}</p>}

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-sm font-medium text-gray-700">Marca</label>
            <select className={selectClass} value={tenantId} onChange={e => setTenantId(e.target.value)}>
              <option value="">Selecione...</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="w-40">
            <label className="mb-1 block text-sm font-medium text-gray-700">Mês</label>
            <select className={selectClass} value={month} onChange={e => setMonth(Number(e.target.value))}>
              {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="w-28">
            <Input label="Ano" type="number" value={year} onChange={e => setYear(Number(e.target.value))} />
          </div>
          <div className="w-40">
            <Input
              label="R$ por unidade"
              type="number"
              step="0.0001"
              min={0}
              value={preco}
              onChange={e => setPreco(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {previa && (
        <Card>
          <CardHeader>
            <span className="text-sm font-semibold text-gray-900">
              Prévia de {MESES[previa.month - 1]} de {previa.year}
            </span>
            <span className="text-xs text-gray-500">nada é gerado até você mandar</span>
          </CardHeader>
          <CardContent className="p-0">
            {previa.ordens.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <FileText className="mx-auto mb-3 h-8 w-8 text-gray-300" />
                <p className="text-sm text-gray-400">
                  Nenhuma ordem encerrada neste mês para esta marca. Só o encerramento entra em
                  fatura — ordem aberta não conta.
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-2.5 text-left font-medium">Ordem</th>
                        <th className="px-4 py-2.5 text-left font-medium">Produto</th>
                        <th className="px-4 py-2.5 text-left font-medium">Lote</th>
                        <th className="px-4 py-2.5 text-left font-medium">Encerrada</th>
                        <th className="px-4 py-2.5 text-right font-medium">Unidades</th>
                        <th className="px-4 py-2.5 text-right font-medium">Valor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {previa.ordens.map(o => (
                        <tr key={o.id}>
                          <td className="px-4 py-2.5 font-mono text-gray-900">{o.number}</td>
                          <td className="px-4 py-2.5 text-gray-700">
                            {o.productName}
                            <span className="block font-mono text-xs text-gray-400">{o.productSku}</span>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-gray-600">{o.lotNumber ?? '—'}</td>
                          <td className="px-4 py-2.5 text-gray-500">{fmtDate(o.closedAt)}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                            {o.qtyProduced.toLocaleString('pt-BR')}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                            {fmtMoney(o.qtyProduced * previa.precoPorUnidade)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t border-gray-200 bg-gray-50">
                      <tr>
                        <td colSpan={4} className="px-4 py-3 text-sm font-medium text-gray-700">
                          {previa.ordens.length} ordem(ns)
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900">
                          {previa.unidades.toLocaleString('pt-BR')}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-base font-semibold text-gray-900">
                          {fmtMoney(previa.total)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
                  <span className="text-xs text-gray-500">
                    Uma linha por ordem — é o que a marca confere.
                  </span>
                  <Button
                    loading={gerar.isPending}
                    disabled={!Number(preco)}
                    onClick={() => { setError(null); gerar.mutate() }}
                  >
                    <Receipt className="h-4 w-4" /> Gerar fatura
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Faturas emitidas</span>
        </CardHeader>
        <CardContent className="p-0">
          {faturas.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Nenhuma fatura de indústria ainda</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Período</th>
                    <th className="px-4 py-2.5 text-left font-medium">Marca</th>
                    <th className="px-4 py-2.5 text-right font-medium">Ordens</th>
                    <th className="px-4 py-2.5 text-right font-medium">Total</th>
                    <th className="px-4 py-2.5 text-left font-medium">Situação</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {faturas.map(f => (
                    <tr key={f.id}>
                      <td className="px-4 py-2.5 text-gray-900">
                        {MESES[f.periodMonth - 1]}/{f.periodYear}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">{f.tenantName}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">{f.itens}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">{fmtMoney(f.total)}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant={STATUS_BADGE[f.status]}>{STATUS_LABEL[f.status]}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex justify-end gap-1">
                          {f.status === 'draft' && (
                            <Button size="sm" variant="secondary"
                              onClick={() => mudarStatus.mutate({ id: f.id, status: 'sent' })}>
                              <Send className="h-4 w-4" /> Enviar
                            </Button>
                          )}
                          {f.status === 'sent' && (
                            <Button size="sm"
                              onClick={() => mudarStatus.mutate({ id: f.id, status: 'paid' })}>
                              <Check className="h-4 w-4" /> Paga
                            </Button>
                          )}
                          {f.status !== 'cancelled' && f.status !== 'paid' && (
                            <Button size="sm" variant="ghost"
                              onClick={() => mudarStatus.mutate({ id: f.id, status: 'cancelled' })}>
                              Cancelar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
